// VIBRANIAM-SPACE v2 - audioNormalizer.js
// Automatic gain control for audio features to prevent overexposure.
// Normalizes RMS to a target value, with smooth attack/release.

export class AudioNormalizer {
  /**
   * @param {number} targetRMS Desired RMS after normalization (0-1). Default 0.2.
   * @param {number} smoothing Smoothing factor for RMS estimation (0-1). Default 0.95.
   */
  constructor(targetRMS = 0.2, smoothing = 0.95) {
    this.targetRMS = targetRMS;
    this.smoothing = smoothing;
    this.smoothedRMS = 0.01; // start low to avoid loud start
  }

  /**
   * Process audio features, returning a normalized copy.
   * @param {object} features Analysis frame from WASM analyzer.
   * @returns {object} Normalized features (bands, rms, bass, mid, high scaled).
   */
  process(features) {
    const rms = features.rms || 0;

    // Attack: fast when RMS rises; release: slow when RMS falls.
    if (rms > this.smoothedRMS) {
      this.smoothedRMS = rms * 0.1 + this.smoothedRMS * 0.9; // attack coefficient 0.1
    } else {
      this.smoothedRMS = rms * 0.02 + this.smoothedRMS * 0.98; // release coefficient 0.02
    }

    // Compute gain to achieve target RMS, but limit to avoid boosting silence.
    let gain = this.targetRMS / Math.max(this.smoothedRMS, 0.001);
    if (gain > 2) gain = 2; // max gain 2x

    // Apply gain to all audio-reactive features.
    const normalized = { ...features };
    if (features.bands) {
      normalized.bands = new Float32Array(features.bands.length);
      for (let i = 0; i < features.bands.length; i++) {
        let v = features.bands[i] * gain;
        // Clamp bands to 1.0 to avoid extreme values (optional).
        if (v > 1) v = 1;
        normalized.bands[i] = v;
      }
    }
    // Scale derived metrics linearly (they are proportional to band energy).
    normalized.rms = features.rms * gain;
    normalized.bass = features.bass * gain;
    normalized.mid = features.mid * gain;
    normalized.high = features.high * gain;
    // onset, flux, centroid, spectrum, peak, dominantFreq, sampleRate, binCount remain unchanged.
    // (Only bands, rms, bass, mid, high, onset are used by substrates.)
    return normalized;
  }

  /** Reset internal state (called on Engine reset). */
  reset() {
    this.smoothedRMS = 0.01;
  }
}